import { parseISO } from "date-fns";
import { Check, ChevronLeft, Copy, MessageSquare, Pencil, Power, PowerOff } from "lucide-react";
import type React from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { copyToClipboard } from "~/common/functions";
import { JsonTable } from "~/components/json-table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  Card,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "~/components/ui";
import type { DataSourceTemplateDetailUI, PropertySchemaRow } from "~/features/dataSourceTemplates/types";

interface DataSourceTemplateDetailPresenterProps {
  template: DataSourceTemplateDetailUI;
  onBack: () => void;
  onEdit: () => void;
  onActivate: () => void;
  onDeactivate: () => void;
  pendingAction: "activate" | "deactivate" | null;
  confirmLabel: string;
  processing: boolean;
  onConfirm: () => void;
  onCancelConfirm: () => void;
}

export function DataSourceTemplateDetailPresenter({
  template,
  onBack,
  onEdit,
  onActivate,
  onDeactivate,
  pendingAction,
  confirmLabel,
  processing,
  onConfirm,
  onCancelConfirm,
}: DataSourceTemplateDetailPresenterProps) {
  const { t } = useTranslation();

  return (
    <div className="mx-auto max-w-[var(--content-max)] space-y-5 p-8">
      <Button type="button" variant="ghost" size="sm" onClick={onBack} className="-ml-2">
        <ChevronLeft />
        <span>{t("ui.backToList")}</span>
      </Button>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-2">
          <h1 className="text-dewey-title">{template.name}</h1>
          {template.isDeactivated && <Badge tone="default">{t("ui.deactivated")}</Badge>}
        </div>

        <div className="flex items-center gap-2">
          <Button type="button" variant="outline" size="sm" onClick={onEdit}>
            <Pencil />
            <span>{t("ui.edit")}</span>
          </Button>
          {template.isDeactivated ? (
            <Button type="button" variant="ghost" size="sm" onClick={onActivate} disabled={processing}>
              <Power />
              <span>{t("ui.activate")}</span>
            </Button>
          ) : (
            <Button type="button" variant="ghost" size="sm" onClick={onDeactivate} disabled={processing}>
              <PowerOff />
              <span>{t("ui.deactivate")}</span>
            </Button>
          )}
        </div>
      </div>

      <SubHeader template={template} />

      <section className="space-y-2">
        <div className="overflow-x-auto rounded-[var(--radius-md)] border border-border-default bg-white">
          <table className="w-full border-collapse">
            <tbody>
              <tr>
                <th className="w-44 bg-bg-subtle px-3.5 py-2 text-left align-top text-12-medium text-fg-2">
                  {t("model.common.name")}
                </th>
                <td className="px-3.5 py-2 text-14-regular text-fg-1">{template.name}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <PropertySchemasSection schemas={template.propertySchemas} />

      <AlertDialog open={pendingAction !== null} onOpenChange={(next) => !next && onCancelConfirm()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("feature.common.confirmTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{confirmLabel}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("ui.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={onConfirm} disabled={processing}>
              {pendingAction === "activate" ? t("ui.activate") : t("ui.deactivate")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function SubHeader({ template }: { template: DataSourceTemplateDetailUI }) {
  const { t } = useTranslation();
  const items: { label: string; value: React.ReactNode }[] = [
    { label: t("model.common.id"), value: <CopyableText text={template.id} className="font-mono" /> },
  ];
  if (template.createdAt) {
    items.push({ label: t("model.common.createdAt"), value: t("date.long", { date: parseISO(template.createdAt) }) });
  }
  if (template.updatedAt && template.updatedAt !== template.createdAt) {
    items.push({ label: t("model.common.updatedAt"), value: t("date.long", { date: parseISO(template.updatedAt) }) });
  }
  if (template.deactivatedAt) {
    items.push({
      label: t("model.common.deactivatedAt"),
      value: t("date.long", { date: parseISO(template.deactivatedAt) }),
    });
  }
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-12-regular text-fg-3">
      {items.map((item, i) => (
        <span key={item.label} className="inline-flex items-center gap-1.5">
          {i > 0 && (
            <span aria-hidden className="text-border-strong">
              |
            </span>
          )}
          <span className="font-medium uppercase tracking-[0.04em]">{item.label}</span>
          <span className="text-fg-2 tabular-nums">{item.value}</span>
        </span>
      ))}
    </div>
  );
}

function CopyableText({ text, className }: { text: string; className?: string }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await copyToClipboard(text);
      setCopied(true);
      toast.success(t("feature.common.idCopied"));
      setTimeout(() => setCopied(false), 1200);
    } catch {
      toast.error(t("feature.common.copyToClipboardFailed"));
    }
  };
  return (
    <span className="inline-flex items-center gap-1">
      <span className={className}>{text}</span>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={onCopy}
            className="inline-flex h-5 w-5 items-center justify-center rounded-[var(--radius-xs)] text-fg-3 hover:bg-bg-subtle hover:text-fg-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-300"
            aria-label={t("ui.copyId")}
          >
            {copied ? <Check className="h-3 w-3 text-success" /> : <Copy className="h-3 w-3" />}
          </button>
        </TooltipTrigger>
        <TooltipContent>{t("ui.copyId")}</TooltipContent>
      </Tooltip>
    </span>
  );
}

function PropertySchemasSection({ schemas }: { schemas: PropertySchemaRow[] }) {
  const { t } = useTranslation();
  return (
    <section className="space-y-2">
      <h2 className="text-16-bold capitalize leading-tight">{t("model.propertySchema.modelNames")}</h2>
      {schemas.length === 0 ? (
        <Card>
          <div className="py-2 text-14-regular text-fg-3">—</div>
        </Card>
      ) : (
        <div className="overflow-x-auto rounded-[var(--radius-md)] border border-border-default bg-white">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-bg-subtle">
                <Th className="w-16 text-center">{t("model.common.position")}</Th>
                <Th>{t("model.common.name")}</Th>
                <Th>{t("model.propertySchema.propertyTypeId")}</Th>
                <Th className="text-center">{t("model.propertySchemaAssociation.required")}</Th>
                <Th className="text-center">{t("model.propertySchemaAssociation.unique")}</Th>
                <Th>{t("model.propertySchemaAssociation.propertyAttributeTypeId")}</Th>
                <Th>{t("ui.options")}</Th>
                <Th className="w-12">{t("model.common.description")}</Th>
              </tr>
            </thead>
            <tbody>
              {schemas.map((s) => (
                <tr key={s.id} className="border-b border-border-default last:border-b-0">
                  <Td className="text-center tabular-nums text-fg-2">{s.position}</Td>
                  <Td className="font-medium text-fg-1">{s.name}</Td>
                  <Td>
                    <Badge tone="default">{s.propertyTypeId}</Badge>
                  </Td>
                  <Td className="text-center">
                    {s.required && <Check className="mx-auto h-3.5 w-3.5 text-success" />}
                  </Td>
                  <Td className="text-center">{s.unique && <Check className="mx-auto h-3.5 w-3.5 text-success" />}</Td>
                  <Td>{s.propertyAttributeTypeId ? <Badge tone="default">{s.propertyAttributeTypeId}</Badge> : "—"}</Td>
                  <Td>
                    <SchemaOptions schema={s} />
                  </Td>
                  <Td className="text-center">
                    {s.description ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            className="inline-flex h-6 w-6 items-center justify-center rounded-[var(--radius-xs)] text-fg-3 hover:bg-bg-subtle hover:text-fg-1"
                            aria-label={s.description}
                          >
                            <MessageSquare className="h-3.5 w-3.5" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent
                          side="left"
                          className="max-w-xs whitespace-normal break-words [overflow-wrap:anywhere]"
                        >
                          {s.description}
                        </TooltipContent>
                      </Tooltip>
                    ) : (
                      <span className="text-fg-3">—</span>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function SchemaOptions({ schema }: { schema: PropertySchemaRow }) {
  const { propertyTypeId, selectOptions, urlAllowList } = schema;
  if (propertyTypeId === "select" || propertyTypeId === "multiSelect") {
    if (!selectOptions || selectOptions.length === 0) return <span className="text-fg-3">—</span>;
    return <JsonTable target={selectOptions} />;
  }
  if (propertyTypeId === "url") {
    if (!urlAllowList || urlAllowList.length === 0) return <span className="text-fg-3">—</span>;
    return (
      <ul className="flex flex-col gap-0.5 font-mono text-12-regular">
        {urlAllowList.map((item, i) => (
          <li key={`${item.value}-${item.regex}-${i}`} className="truncate">
            {item.regex ? `regex: ${item.regex}` : item.value}
          </li>
        ))}
      </ul>
    );
  }
  return <span className="text-fg-3">—</span>;
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <th
      className={`border-b border-border-default px-3.5 py-2.5 text-left text-10-regular uppercase tracking-[0.06em] text-fg-2 ${className ?? ""}`}
    >
      {children}
    </th>
  );
}

function Td({ children, className }: { children: React.ReactNode; className?: string }) {
  return <td className={`px-3.5 py-2 text-14-regular align-middle text-fg-1 ${className ?? ""}`}>{children}</td>;
}
